import { useSyncExternalStore } from 'react';

export type Theme = 'light' | 'dark';

/**
 * Keep in step with the inline script in `index.html`, which applies the theme
 * before first paint so a dark-mode user never sees a white flash.
 */
export const THEME_STORAGE_KEY = 'bulk3-admin-theme';

const DARK_QUERY = '(prefers-color-scheme: dark)';

const listeners = new Set<() => void>();

function readStored(): Theme | null {
  try {
    const value = localStorage.getItem(THEME_STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    // Private windows and blocked storage throw; fall back to the OS.
    return null;
  }
}

function systemTheme(): Theme {
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
}

/** An explicit choice wins; until one is made the OS preference is followed live. */
function resolveTheme(): Theme {
  return readStored() ?? systemTheme();
}

function apply(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', theme === 'dark' ? '#111a2c' : '#17315c');
}

function emit(): void {
  apply(resolveTheme());
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  if (listeners.size === 0) {
    window.matchMedia(DARK_QUERY).addEventListener('change', emit);
  }
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      window.matchMedia(DARK_QUERY).removeEventListener('change', emit);
    }
  };
}

export function setTheme(theme: Theme): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Not persisted, but the switch still applies for this page.
    apply(theme);
    listeners.forEach((listener) => listener());
    return;
  }
  emit();
}

export function useTheme(): { theme: Theme; toggleTheme: () => void } {
  const theme = useSyncExternalStore(subscribe, () =>
    document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light',
  );

  return { theme, toggleTheme: () => setTheme(theme === 'dark' ? 'light' : 'dark') };
}
